import { Access, AccessFeature, AccessFeatures, DealerType } from "@biz/definitions/AccessDefinitions";
import { OrderState } from "@biz/definitions/DatabaseDefinitions";
import { AppUser, IAppUser } from "@biz/models/AppUser";
import { IProjectInfo } from "@biz/projectCalculation/ProjectDefinitions";
import { ILocalProject } from "@biz/userProjects/new/IProject";
import { IProjectDealer } from "@biz/userProjects/new/IProjectDefinitions";

export type AppUserInput = IAppUser | { app: IAppUser } | undefined;
export type AppUserAccessInput = AppUserInput | Access;
export class AccessHelper {
  static roofsVisible(access: Access): boolean {
    return access >= Access.Monteur;
  }

  static openingsVisible(access: Access): boolean {
    return access >= Access.Monteur;
  }

  static canSeeShop(user: AppUserInput): boolean {
    return AccessHelper.hasAccess(user, Access.Monteur);
  }

  static showPrice(user: AppUserAccessInput): boolean {
    return AccessHelper.hasAccess(user, Access.Dealer);
  }

  static showErrors(user: AppUserAccessInput): boolean {
    return AccessHelper.hasAccess(user, Access.Monteur);
  }

  static canSeeExternalImporter(loggedInUser: IAppUser | undefined): boolean {
    return AccessHelper.hasFeature(loggedInUser, AccessFeature.ExternalImporter) || (loggedInUser?.companyId === 25338); // pina
  }

  static canSee3dConfigurator(loggedInUser: IAppUser | undefined): boolean {
    return this.hasAccess(loggedInUser, Access.Customer);
  }


  static canSeeSailConfigurator(loggedInUser: IAppUser | undefined): boolean {
    const dealerType = AppUser.getDealerType(loggedInUser);
    return this.hasAccess(loggedInUser, Access.Monteur) && [DealerType.RS, DealerType.SO].includes(dealerType!);
  }

  static canCreateSunSailProject(loggedInUser: IAppUser | undefined): boolean {
    return this.hasAccess(loggedInUser, Access.Dealer);
  }

  static canViewNote(loggedInUser: IAppUser | undefined): boolean {
    return this.hasAccess(loggedInUser, Access.Soliday);
  }

  static canViewNotes(loggedInUser: IAppUser | undefined): boolean {
    return AccessHelper.hasFeature(loggedInUser, AccessFeature.CanViewNotes);
  }

  static canEditNote(loggedInUser: IAppUser | undefined): boolean {
    return this.hasAccess(loggedInUser, Access.Soliday);
  }

  static canDeleteNote(loggedInUser: IAppUser | undefined, noteUserId: string | undefined): boolean {
    if (!loggedInUser) return false;
    if (AccessHelper.hasAccess(loggedInUser, Access.SolidayAdmin)) return true;
    if (loggedInUser._id === noteUserId) return true;
    return false;
  }

  static cancChangeFriendCompanies(loggedInUser: IAppUser | undefined): boolean {
    return this.hasAccess(loggedInUser, Access.SolidayAdmin);
  }

  static canLockDealer(loggedInUser: IAppUser | undefined): boolean {
    return this.hasAccess(loggedInUser, Access.SolidayPro);
  }

  static canChangeFocusDealer(loggedInUser: IAppUser | undefined): boolean {
    return this.hasAccess(loggedInUser, Access.SolidayPro);
  }

  static canChangeCompanyAdditionalInfo(loggedInUser: IAppUser | undefined): boolean {
    return this.hasAccess(loggedInUser, Access.Soliday);
  }

  static hasAccessChangeShowSail(loggedInUser: IAppUser | undefined): boolean {
    return this.hasAccess(loggedInUser, Access.Soliday);
  }

  static hasAccessChangeCompetitorProducts(loggedInUser: IAppUser | undefined): boolean {
    return this.hasAccess(loggedInUser, Access.Soliday);
  }

  static hasPowerderCoating(loggedInUser: IAppUser | undefined): boolean {
    return this.hasAccess(loggedInUser, Access.Soliday);
  }

  static canChangeUserAccess(userToChange: IAppUser | undefined, loggedInUser: IAppUser | undefined): boolean {
    if (!userToChange) return false;
    if (!loggedInUser) return false;

    if (AccessHelper.isSuperUser(loggedInUser)) return true;

    if (loggedInUser._id === userToChange._id) return false;
    if (AppUser.getAccess(userToChange) >= AppUser.getAccess(loggedInUser)) return false;
    if (AccessHelper.hasFeature(loggedInUser, AccessFeature.ChangeUserAccess)) return true;
    return false;
  }

  static canChangeUserResponsibility(userToChange: IAppUser | undefined, loggedInUser: IAppUser | undefined): boolean {
    if (!userToChange) return false;
    if (!loggedInUser) return false;

    if (AccessHelper.isSuperUser(loggedInUser)) return true;

    if (loggedInUser._id === userToChange._id) return false;
    if (AppUser.getAccess(userToChange) >= AppUser.getAccess(loggedInUser)) return false;
    if ((AccessHelper.hasAccess(loggedInUser, Access.Soliday)) && AccessHelper.hasFeature(loggedInUser, AccessFeature.ChangeResponsibilityArea)) return true;

    return false;
  }

  static canSeeUserStatistics(loggedInUser: IAppUser | undefined): boolean {
    if (!loggedInUser) return false;

    if (AccessHelper.hasSolidayAccess(loggedInUser)) return true;

    return false;
  }

  static hasSolidayAccess(user: IAppUser | undefined): boolean {
    return AccessHelper.hasAccess(user, Access.Soliday);
  }

  static hasDealerAccess(user: IAppUser | undefined): boolean {
    return AccessHelper.hasAccess(user, Access.Dealer);
  }


  static hasAccess(appUser: AppUserAccessInput, targetAccess: Access): boolean {
    return AccessHelper.getAccess(appUser) >= targetAccess;
  }

  static getAccess(appUser: AppUserAccessInput): Access {
    if (!appUser)
      return 0;
    if (typeof appUser === "number")
      return appUser;
    if ("app" in appUser)
      return AppUser.getAccess(appUser?.app)
    return AppUser.getAccess(appUser);
  }

  static canEditOrderState(_sceneState: OrderState, _orderTime: number, _buyerCompanyId: string, _targetState: OrderState, loggedInUser: IAppUser | undefined): boolean {
    // if (getSceneStateNumber(targetState) > 5)
    // return false;
    if (!loggedInUser) return false;
    if (this.hasFeature(loggedInUser, AccessFeature.CanEditOrderState))
      return true;
    // if (loggedInUser.access >= Access.Soliday)
    //   return true;

    // if (loggedInUser.access < Access.Dealer) return false;
    // const deltaTime = Date.now() - orderTime; //
    // /*if (deltaTime < 1000 * 60 * 60) {
    //   if (loggedInUser.access >= Access.Soliday) {
    //     return (
    //       targetState === OrderState.Cancelled || targetState === sceneState || targetState === OrderState.Ordered
    //     );
    //   }
    // }*/
    // if (deltaTime < 1000 * 60 * 60) {
    //   if (loggedInUser.companyId.toString() === buyerCompanyId && loggedInUser.access >= Access.Dealer) {
    //     return targetState === OrderState.Cancelled || targetState === sceneState || targetState === OrderState.Ordered;
    //   }
    // }
    return false;
  }

  static canViewProject(projectDealer: IProjectDealer, loggedInUser: IAppUser | undefined): boolean {
    if (!loggedInUser) return false;
    if (AccessHelper.hasAccess(loggedInUser, Access.Soliday)) return true;
    if (projectDealer.userId === loggedInUser._id) return true;
    if (projectDealer.companyId === AppUser.getCompanyId(loggedInUser)) return true;
    return false;
  }

  static canOrder2dProject(projectDealer: IProjectDealer, loggedInUser: IAppUser | undefined): boolean {
    if (!loggedInUser) return false;
    if (AccessHelper.hasAccess(loggedInUser, Access.Soliday)) return true;
    if (projectDealer.userId === loggedInUser._id) return true;
    if (projectDealer.companyId === AppUser.getCompanyId(loggedInUser)) return true;
    return false;
  }

  static canOrderWithSeriousError(loggedInUser: IAppUser | undefined): boolean {
    return AccessHelper.hasFeature(loggedInUser, AccessFeature.CanOrderSeriousError);
  }

  // static canEditProjectItem(project: IProjectItem, loggedInUser: IAppUser | undefined): boolean {
  //   if (!loggedInUser) return false;
  //   if (project.owner === loggedInUser._id) return true;
  //   if (loggedInUser.access >= Access.Soliday) return true;
  //   if (project.data.dealer.userId === loggedInUser._id) return true;
  //   // if (project.data.dealer.companyId === loggedInUser.companyId)
  //   //  return true;
  //   return false;
  // }

  static canEditProject(project: ILocalProject, loggedInUser: IAppUser | undefined): boolean {
    if (!loggedInUser) return false;
    if (project.owner === loggedInUser._id) return true;
    if (AccessHelper.hasAccess(loggedInUser, Access.Soliday)) return true;
    if (project.dealer.userId === loggedInUser._id) return true;
    // if (project.data.dealer.companyId === loggedInUser.companyId)
    //  return true;
    return false;
  }

  static canSendNewPassword(userToSend: IAppUser | undefined, loggedInUser: IAppUser | undefined): boolean {
    if (!userToSend) return false;
    if (!loggedInUser) return false;
    const loggedInAccess = AppUser.getAccess(loggedInUser);
    if (loggedInAccess < Access.Soliday) return false;
    if (userToSend._id === loggedInUser._id) return false;
    const userToSendAccess = AppUser.getAccess(userToSend);
    if (userToSendAccess >= loggedInAccess) return false;
    return true;
  }

  static canGenerate3dLinks(loggedInUser: IAppUser | undefined): boolean {
    return this.hasAccess(loggedInUser, Access.Soliday);
  }

  static canResetDeviceIds(loggedInUser: IAppUser | undefined): boolean {
    return this.hasAccess(loggedInUser, Access.Soliday);
  }

  static canSeeSales(loggedInUser: IAppUser | undefined): boolean {
    return AccessHelper.hasFeature(loggedInUser, AccessFeature.CanSeeSales);
  }

  static userCanSeeMedia(user: { access: Access; dealerType?: DealerType } | undefined, media: { dealerTypes?: DealerType[]; access: Access }): boolean {
    return media.access <= (user?.access || 0) && this.isValidDealerType(AppUser.getDealerType(user), media.dealerTypes);
  }

  static supportsFavorites(user: AppUserAccessInput): boolean {
    return AccessHelper.hasAccess(user, Access.Dealer);
  }

  static userCanSeeArticle(user: { access: Access; dealerType?: DealerType } | undefined, media: { dealerTypes?: DealerType[]; access?: Access }): boolean {
    const userAccess = user?.access || Access.Public;
    const access = media.access || Access.Public;
    const dealerType = AppUser.getDealerType(user);
    return access <= userAccess && this.isValidDealerType(dealerType, media.dealerTypes);
  }

  static isValidDealerType(userType?: DealerType, dealerTypes?: DealerType[]): boolean {
    if (!dealerTypes) return true;
    if (!userType) return !dealerTypes?.length;
    return dealerTypes.includes(userType);
  }

  static calcDealerTypeFromIndustry(industry?: string): DealerType {
    return (industry || "") as DealerType;
  }

  static hasAccessResponsibilityFilter(user: IAppUser | undefined): boolean {
    if (!user) return false;
    if (AppUser.getAccess(user) < Access.Soliday) return false;
    if (user.responsibilityFilterId) return AccessHelper.hasFeature(user, AccessFeature.IgnoreResponsibilityArea);
    return true;
  }

  static isSuperUser(user: IAppUser | undefined): boolean {
    return (user?.access || 0) >= Access.Dev;
  }

  static canHaveFeatures(user: { access: Access } | undefined): boolean {
    return !!user && user?.access >= Access.Soliday;
  }

  static hasAllFeatures(user: { access: Access } | undefined): boolean {
    return !!user && user.access >= Access.SolidayAdmin;
  }

  static hasFeature(user: IAppUser | undefined, feature: AccessFeature): boolean {
    if (!this.canHaveFeatures(user)) return false;
    if (this.hasAllFeatures(user)) return true;
    return this.isInFeatures(feature, user?.accessFeatures);
  }

  static isInFeatures(feature: AccessFeature, featureList: AccessFeatures | undefined): boolean {
    const f = featureList?.[feature] || 0;
    return f > 0;
  }

  static toggleFeature(feature: AccessFeature, featureList: AccessFeatures | undefined): AccessFeatures {
    if (this.isInFeatures(feature, featureList)) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [feature]: value, ...rest } = featureList as any;
      return rest;
    } else {
      return { ...featureList, [feature]: 1 };
    }
  }

  static canSeeUserProjects(viewer: IAppUser | undefined, otherUserId: string, otherUserAccess: Access | undefined): boolean {
    if (!viewer)
      return false;
    if (viewer._id === otherUserId)
      return true;
    const access = AppUser.getAccess(viewer);
    if (otherUserAccess !== undefined && access > otherUserAccess && access >= Access.Soliday)
      return true;
    return false;
  }

  static filterInfos(infos: IProjectInfo[] | undefined, access: Access): IProjectInfo[] {
    if (!infos)
      return [];
    return infos.filter((inf) => inf.access <= access)
  }
}
